import React, {useState, useEffect, ReactNode, HTMLAttributes} from 'react'
import {cn} from '@/lib/utils'

interface DelayedAccordionContentProps extends HTMLAttributes<HTMLDivElement> {
    isOpen: boolean
    children: ReactNode
    animationDelay: number
    className?: string
    'data-testid': string | undefined
}

export const DelayedAccordionContent: React.FC<DelayedAccordionContentProps> = ({
    isOpen,
    children,
    className,
    animationDelay,
    'data-testid': dataTestId,
    ...props
}) => {
    const [isHidden, setIsHidden] = useState(!isOpen)
    const delayOffset = animationDelay * 0.85 // Add a small offset to ensure content is hidden before animation starts

    useEffect(() => {
        if (!isOpen) {
            // Delay hiding the content to allow animation to complete
            const timeout = setTimeout(() => setIsHidden(true), delayOffset) // Adjust delay to match animation duration
            return () => clearTimeout(timeout)
        } else {
            setIsHidden(false) // Show content immediately when opened
        }
    }, [isOpen])

    return (
        <div
            {...props}
            data-testid={dataTestId || 'DelayedAccordionContent'}
            className={cn(
                className,
                !isOpen &&
                    isHidden &&
                    `animate-[fade-out-up_ease-in-out] duration-${delayOffset} hidden`,
                !isOpen && `opacity-0 transition-opacity duration-${delayOffset}` // Smooth fade-out
            )}
        >
            {children}
        </div>
    )
}
